import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import { Image, Upload, Trash2, Eye, EyeOff } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { useToast } from '../../contexts/ToastContext';
import {
  getAdminBackgrounds,
  uploadBackground,
  updateBackground,
  deleteBackground,
  type AdminBackground,
} from '../../services/backgroundService';
import styles from './FondosVirtualesPage.module.css';

export default function FondosVirtualesPage() {
  const { addToast } = useToast();
  const [backgrounds, setBackgrounds] = useState<AdminBackground[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Load backgrounds
  useEffect(() => {
    loadBackgrounds();
  }, []);

  async function loadBackgrounds() {
    try {
      setLoading(true);
      const bgs = await getAdminBackgrounds();
      setBackgrounds(bgs);
    } catch (err) {
      addToast('error', 'Error cargando fondos virtuales');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e: FormEvent) {
    e.preventDefault();
    if (!newFile || !newName.trim()) {
      addToast('error', 'Ingrese un nombre y seleccione un archivo.');
      return;
    }

    setUploading(true);
    try {
      const order = backgrounds.length;
      const bg = await uploadBackground(newFile, newName.trim(), order);
      setBackgrounds((prev) => [...prev, bg]);
      setNewName('');
      setNewFile(null);
      if (fileRef.current) fileRef.current.value = '';
      addToast('success', `Fondo "${bg.name}" subido correctamente.`);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al subir');
    } finally {
      setUploading(false);
    }
  }

  async function handleToggleActive(bg: AdminBackground) {
    try {
      await updateBackground(bg.id, { active: !bg.active });
      setBackgrounds((prev) =>
        prev.map((b) => (b.id === bg.id ? { ...b, active: !b.active } : b)),
      );
      addToast('success', `Fondo "${bg.name}" ${bg.active ? 'desactivado' : 'activado'}.`);
    } catch {
      addToast('error', 'Error al actualizar fondo');
    }
  }

  async function handleDelete(bg: AdminBackground) {
    if (!confirm(`¿Eliminar el fondo "${bg.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteBackground(bg);
      setBackgrounds((prev) => prev.filter((b) => b.id !== bg.id));
      addToast('success', `Fondo "${bg.name}" eliminado.`);
    } catch {
      addToast('error', 'Error al eliminar fondo');
    }
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setNewFile(file);
    // Auto-fill name from filename if empty
    if (file && !newName.trim()) {
      const nameWithoutExt = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');
      setNewName(nameWithoutExt);
    }
  }

  return (
    <MainLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <Image size={22} />
          <h1 className={styles.title}>Fondos Virtuales</h1>
        </div>

        {/* Upload Section */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Subir nuevo fondo</h2>
          <div className={styles.sectionBody}>
            <p className={styles.sectionDescription}>
              Suba imágenes que aparecerán como opciones de fondo virtual para los médicos
              durante la teleconsulta. Formato recomendado: JPEG o PNG de 1280×720px (16:9).
            </p>
            <form className={styles.uploadForm} onSubmit={handleUpload}>
              <div className={styles.uploadField}>
                <label htmlFor="bg-name">Nombre</label>
                <input
                  id="bg-name"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Ej: Oficina MINSA"
                  maxLength={50}
                />
              </div>
              <div className={styles.uploadField}>
                <label htmlFor="bg-file">Imagen</label>
                <input
                  ref={fileRef}
                  id="bg-file"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className={styles.fileInput}
                  onChange={handleFileChange}
                />
              </div>
              <button type="submit" className={styles.uploadBtn} disabled={uploading || !newFile}>
                <Upload size={14} />
                {uploading ? 'Subiendo...' : 'Subir fondo'}
              </button>
            </form>
          </div>
        </section>

        {/* Existing Backgrounds */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            Fondos administrados ({backgrounds.length})
          </h2>
          <div className={styles.sectionBody}>
            {loading ? (
              <p className={styles.emptyState}>Cargando...</p>
            ) : backgrounds.length === 0 ? (
              <p className={styles.emptyState}>
                No hay fondos personalizados. Los médicos verán solo los fondos predeterminados del sistema.
              </p>
            ) : (
              <div className={styles.bgGrid}>
                {backgrounds.map((bg) => (
                  <div
                    key={bg.id}
                    className={`${styles.bgCard} ${!bg.active ? styles.bgCardInactive : ''}`}
                  >
                    <img
                      src={bg.downloadUrl}
                      alt={bg.name}
                      className={styles.bgPreview}
                      loading="lazy"
                    />
                    <div className={styles.bgCardBody}>
                      <p className={styles.bgCardName}>{bg.name}</p>
                      <div className={styles.bgCardActions}>
                        <button
                          type="button"
                          className={styles.bgToggleBtn}
                          onClick={() => handleToggleActive(bg)}
                          title={bg.active ? 'Desactivar' : 'Activar'}
                        >
                          {bg.active ? <Eye size={12} /> : <EyeOff size={12} />}
                          {bg.active ? 'Activo' : 'Inactivo'}
                        </button>
                        <button
                          type="button"
                          className={styles.bgDeleteBtn}
                          onClick={() => handleDelete(bg)}
                          title="Eliminar"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
